import axios, { rawAxios } from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  AccountingJournalReportFilters,
  CashDrawerReportFilters,
  ClosedDaysReportFilters,
  CustomerBalanceReportFilters,
  ExternalOrdersReportFilters,
  FloristPerformanceReportFilters,
  PhoneClickReportFilters,
  SalesSummaryReportFilters,
  SalesTaxReportFilters,
  TipsReportFilters,
} from './reports.schema'
import type {
  AccountingJournalOrder,
  AccountingJournalReport,
  CashDrawerReport,
  ClosedDaysReport,
  CustomerBalanceReport,
  ExternalOrdersReport,
  FloristMapReport,
  FloristPerformanceReport,
  PhoneClickReport,
  SalesSummaryReport,
  SalesTaxReport,
  TipsReport,
} from './reports.types'

/** The key entries every report fetcher reads. */
export interface ReportQueryKeyEntries<TFilters> {
  organizationId: OrganizationId
  filters?: TFilters | undefined
}

/** Report fetchers read only the query key. */
type ReportQueryContext<TFilters> = Pick<
  QueryFunctionContext<[ReportQueryKeyEntries<TFilters>]>,
  'queryKey'
>

/** What every spreadsheet export takes. */
export interface ReportExportRequest<TFilters> {
  organizationId: OrganizationId
  filters?: TFilters | undefined
}

const SPREADSHEET_ACCEPT = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export async function getAccountingJournalReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<AccountingJournalReportFilters>) {
  return axios.get<AccountingJournalReport>(
    '/reports/accounting-journal',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

/** Everything one order has posted; `filters.salesOrderId` names the order. */
export async function getAccountingJournalOrder({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<{ salesOrderId: number }>) {
  return axios.get<AccountingJournalOrder>(
    `/reports/accounting-journal/orders/${filters?.salesOrderId ?? ''}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function exportAccountingJournalReport({
  organizationId,
  filters,
}: ReportExportRequest<AccountingJournalReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/accounting-journal/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getClosedDaysReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<ClosedDaysReportFilters>) {
  return axios.get<ClosedDaysReport>(
    '/reports/closed-days',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function exportClosedDaysReport({
  organizationId,
  filters,
}: ReportExportRequest<ClosedDaysReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/closed-days/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getPhoneClickReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<PhoneClickReportFilters>) {
  return axios.get<PhoneClickReport>(
    '/reports/phone-clicks',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function exportPhoneClickReport({
  organizationId,
  filters,
}: ReportExportRequest<PhoneClickReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/phone-clicks/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getCustomerBalanceReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<CustomerBalanceReportFilters>) {
  return axios.get<CustomerBalanceReport>(
    '/reports/customer-balances',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function exportCustomerBalanceReport({
  organizationId,
  filters,
}: ReportExportRequest<CustomerBalanceReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/customer-balances/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getSalesTaxReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<SalesTaxReportFilters>) {
  return axios.get<SalesTaxReport>(
    '/reports/sales-tax',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function exportSalesTaxReport({
  organizationId,
  filters,
}: ReportExportRequest<SalesTaxReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/sales-tax/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getSalesSummaryReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<SalesSummaryReportFilters>) {
  return axios.get<SalesSummaryReport>(
    '/reports/sales-summary',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function exportSalesSummaryReport({
  organizationId,
  filters,
}: ReportExportRequest<SalesSummaryReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/sales-summary/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getTipsReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<TipsReportFilters>) {
  return axios.get<TipsReport>(
    '/reports/tips',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function exportTipsReport({
  organizationId,
  filters,
}: ReportExportRequest<TipsReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/tips/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getExternalOrdersReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<ExternalOrdersReportFilters>) {
  return axios.get<ExternalOrdersReport>(
    '/reports/external-orders',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function exportExternalOrdersReport({
  organizationId,
  filters,
}: ReportExportRequest<ExternalOrdersReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/external-orders/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getCashDrawerReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<CashDrawerReportFilters>) {
  return axios.get<CashDrawerReport>(
    '/reports/cash-drawer',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function exportCashDrawerReport({
  organizationId,
  filters,
}: ReportExportRequest<CashDrawerReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/cash-drawer/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}

export async function getFloristPerformanceReport({
  queryKey: [{ organizationId, filters }],
}: ReportQueryContext<FloristPerformanceReportFilters>) {
  return axios.get<FloristPerformanceReport>(
    '/reports/florist-performance',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function getFloristMapReport({
  queryKey: [{ organizationId }],
}: ReportQueryContext<undefined>) {
  return axios.get<FloristMapReport>(
    '/reports/florist-map',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function exportFloristPerformanceReport({
  organizationId,
  filters,
}: ReportExportRequest<FloristPerformanceReportFilters>) {
  return rawAxios.get<Blob>(
    '/reports/florist-performance/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: SPREADSHEET_ACCEPT,
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}
